import { useCallback, type ReactNode } from 'react'
import { isEmpty, type EntityId, type Preferences } from '@terros/common'
import { notifications } from '@mantine/notifications'
import { MultiSelect, type MultiSelectProps } from '@mantine/core'
import { useToastError } from '@hooks'
import { useTranslation, useUpdatePreference, type SelectItem } from '@front/model'
import { PreferenceInput } from './PreferenceInput'

type MultiSelectKeys = keyof Pick<
  Preferences,
  | 'leaderboardColumns'
  | 'defaultStatsColumns'
  | 'hiddenCanvassMapLayers'
  | 'hiddenCanvassUpsells'
  | 'lockedUserFields'
  | 'lockedTeamFields'
>

type Props<K extends MultiSelectKeys> = Omit<
  MultiSelectProps,
  'data' | 'value' | 'onChange' | 'label' | 'description'
> & {
  data: SelectItem[]
  preference: K
  targetId: EntityId
  description?: ReactNode
}

export const PreferenceMultiSelect = <K extends MultiSelectKeys>({
  preference,
  targetId,
  disabled,
  description: customDescription,
  ...rest
}: Props<K>): ReactNode => {
  const { t } = useTranslation()
  const { update, error: updateError } = useUpdatePreference(preference, targetId)
  useToastError(updateError)

  const setValue = useCallback(
    async (value: string[]) => {
      await update(value as Preferences[K])
      notifications.show({ message: t('Preference saved'), color: 'green' })
    },
    [update, t]
  )

  return (
    <PreferenceInput targetId={targetId} preference={preference}>
      {({ data, disabled: isReadOnly }) => (
        <MultiSelect
          {...rest}
          disabled={disabled || isReadOnly}
          flex={1}
          value={toStringArray(data.current ?? data.inherited)}
          onChange={setValue}
        />
      )}
    </PreferenceInput>
  )
}

const toStringArray = (value: string[] | readonly string[] | undefined): string[] => {
  if (isEmpty(value)) return []
  return [...value]
}
